import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AuthService } from '../../../../../../../shared/services';
import { FetchWarrantiesRequest } from '../../../../../core/projects.actions';
import { warrantiesInfo } from '../../../../../core/projects.selectors';

@Component({
  selector: 'app-warranty-wrapper',
  templateUrl: './warranty-wrapper.component.html',
  styleUrls: ['./warranty-wrapper.component.scss'],
  standalone: false,
})
export class WarrantyWrapperComponent implements OnInit, OnDestroy {
  activeSubTab: 'settings' | 'tasks' | 'claims' | 'packages' = 'settings';
  isContractor = false;
  hasWarranties = false;
  private lastSubTabParam: string = null;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private store: Store,
  ) {}

  ngOnInit() {
    // Detect contractor role
    const url = this.router.url;
    const segments = url.split('?')[0].split('/');
    const projectId = segments[segments.indexOf('projects') + 1] || '';
    const account = this.authService.getCurrentUser();
    const role = account?.roles?.find(r => r.project_id === projectId)?.role || account?.role || '';
    this.isContractor = role?.startsWith('contractor');

    // Fetch warranties to know if Claims tab should show
    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId } }));
    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        this.hasWarranties = (info?.list?.length || 0) > 0;
        // Re-apply the deep-linked sub-tab: on a cold load the queryParams
        // emission races this fetch and hasWarranties is still false.
        this.applySubTab();
      });

    // Set default tab (before queryParams overrides)
    if (this.isContractor) {
      this.activeSubTab = 'claims';
    }

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.lastSubTabParam = qp.subTab || null;
        this.applySubTab();
      });
  }

  private applySubTab(): void {
    const subTab = this.lastSubTabParam;
    if (subTab === 'claims' && this.hasWarranties) {
      this.activeSubTab = 'claims';
    } else if (subTab === 'tasks' && this.hasWarranties && !this.isContractor) {
      this.activeSubTab = 'tasks';
    } else if (subTab === 'packages' && this.hasWarranties) {
      this.activeSubTab = 'packages';
    } else if (this.isContractor) {
      this.activeSubTab = 'claims';
    } else {
      this.activeSubTab = 'settings';
    }
  }

  selectSubTab(subTab: 'settings' | 'tasks' | 'claims' | 'packages'): void {
    // Persist the sub-tab in the URL so merged query params (status filters etc.)
    // don't accidentally knock us back to 'settings'.
    this.router.navigate([], {
      queryParams: { subTab, status: null },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
